import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import CloseButton from 'ext-ui/components/CloseButton/CloseButton'
import React, { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { AnalyticsPageName } from 'src/analytics/analytics.models'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import BetaTag from 'src/components/BetaTag/BetaTag'
import Box from 'src/components/Box/Box'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import SvgTransaction from 'src/components/Svgs/SvgTransaction'
import TransactionExpenseDetailsForm from 'src/components/TransactionExpenseDetailsForm/TransactionExpenseDetailsForm'
import { useChangedEffect, useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { ExpenseDetailsModel } from 'src/models/transaction.models'
import { closeModal } from 'src/navigation/Lnk'
import {
  activeAlphabeticalExpenseCategoryFiltersRequestParams,
  ExpenseCategoryType,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import {
  usePatchBulkTransactionExpenseData,
  useQueryFetchedTransactions,
} from 'src/queries/transactions.queries'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import {
  selectIsSelectAllTransactionsFromState,
  selectSelectAllEditableTransactionsFiltersFromState,
  selectSelectedTransactionIdsFromState,
} from 'src/redux/selectors/data/transactions.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import {
  mapExpDetailsToNulls,
  PatchBulkTransactionExpenseDataRequest,
} from 'src/requests/patchTransactionExpenseData.request'
import { css } from 'styled-components'

const UpdateBulkTransactionsFlyout: React.FC = () => {
  const translate = useTranslate()

  const dispatch = useFlyoutDispatch()
  const appName = useSelector(selectAppName)
  const selectedTransactionIds = useSelector(
    selectSelectedTransactionIdsFromState,
  )
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const { data } = useQueryFetchedTransactions()
  const selectedTransactions = useMemo(
    () =>
      (data?.transactions ?? []).filter((txn) =>
        selectedTransactionIds.has(txn.id),
      ),
    [data, selectedTransactionIds],
  )
  const selectedEditableTransactions = useMemo(
    () =>
      selectedTransactions.filter(
        (txn) =>
          txn.notPushedToConnectedPlatform(
            activeIntegration?.connectedPlatform,
          ) && !txn.isSplit,
      ),
    [selectedTransactions],
  )
  const selectAllEditableTransactionsFiltersBody = useSelector(
    selectSelectAllEditableTransactionsFiltersFromState,
  )
  const allSelected = useSelector(selectIsSelectAllTransactionsFromState)

  const { analytics } = useAnalyticsContext()
  useEffect((): void => {
    analytics?.page(AnalyticsPageName.BulkEditTransactions)
  }, [])

  const { data: orgExpCats } = useQueryExpenseCategories({
    filters: activeAlphabeticalExpenseCategoryFiltersRequestParams,
  })

  // treat all fields as optional for bulk updates
  const orgExpenseCategories = useMemo(
    () =>
      orgExpCats?.map((expCat) => ({
        ...expCat,
        requiredForTransactions: false,
      })),
    [orgExpCats],
  )

  // baseValues
  const transactionsExpCats = useMemo(() => {
    const categoryMapRaw: Record<
      string,
      Array<ExpenseDetailsModel | undefined>
    > = selectedEditableTransactions
      .flatMap((txn) => txn.expenseDetails)
      .reduce<Record<string, Array<ExpenseDetailsModel | undefined>>>(
        (acc, expenseDetail) => {
          if (expenseDetail === undefined) return acc
          // TODO: clean up when backend normalizes expense details for customer/vendor category
          const isCustomerOrVendor =
            expenseDetail.categoryType === ExpenseCategoryType.Customer ||
            expenseDetail.categoryType === ExpenseCategoryType.Vendor
          const schemaEC = isCustomerOrVendor
            ? orgExpenseCategories?.find(
                (ec) => ec.type === expenseDetail.categoryType,
              )
            : undefined

          const categoryId =
            isCustomerOrVendor && schemaEC
              ? schemaEC.id
              : expenseDetail.categoryId

          // ensure entry exists in mapping
          if (!acc[categoryId]) acc[categoryId] = []

          // remove ruleId and errorMessage from expenseDetail in bulk use case
          acc[categoryId].push({
            ...expenseDetail,
            ruleId: undefined,
            errorMessage: undefined,
          })

          return acc
        },
        {},
      )

    const categoryMap: Record<
      string,
      Array<ExpenseDetailsModel | undefined>
    > = Object.entries(categoryMapRaw).reduce<
      Record<string, Array<ExpenseDetailsModel | undefined>>
    >((acc, [categoryId, expDetails]) => {
      // if all labels are mapped as the same value, only show one
      const allLabelsSame =
        expDetails.length > 0 &&
        // dropdown label options
        (expDetails.every(
          (expDetail) =>
            expDetail?.labelId !== undefined &&
            expDetail?.labelId === expDetails[0]?.labelId,
        ) ||
          // free text label options
          expDetails.every(
            (expDetail) =>
              expDetail?.labelId === undefined &&
              expDetail?.labelName === expDetails[0]?.labelName,
          ))
      const allCategoriesMapped =
        expDetails.length === selectedEditableTransactions.length
      if (allLabelsSame && allCategoriesMapped) {
        acc[categoryId] = [expDetails[0]]
      } else if (allLabelsSame) {
        // if all labels are the same, but some weren't mapped, add undefined to represent an empty entry
        acc[categoryId] = [expDetails[0], undefined]
      } else {
        acc[categoryId] = expDetails
      }

      return acc
    }, {})

    return Object.values(categoryMap)
  }, [selectedEditableTransactions, orgExpenseCategories])

  const {
    mutate: patchBulkTransactionExpenseData,
    isPending: puttingBulkTransactionExpenseData,
  } = usePatchBulkTransactionExpenseData()

  const dispatchPatchBulkTransactionExpenseData = (
    body: Partial<PatchBulkTransactionExpenseDataRequest>,
  ): void => {
    patchBulkTransactionExpenseData({
      filters: selectAllEditableTransactionsFiltersBody,
      body: {
        ...body,
        transactionIds: allSelected
          ? undefined
          : selectedEditableTransactions.map((txn) => txn.id),
      },
    })

    // calculate base values to determine if the dropdowns were changed or not
    // to report as analytics
    const singleBaseTransactionsExpCats = transactionsExpCats?.reduce(
      (acc, curr) => {
        const baseVal =
          curr !== undefined && curr.length === 1
            ? Array.from(curr)[0]
            : undefined
        return [...acc, baseVal]
      },
      [],
    )
    const updateExpCatsCount =
      orgExpenseCategories !== undefined
        ? orgExpenseCategories.reduce((acc, curr) => {
            const oldVal = singleBaseTransactionsExpCats?.find(
              (cat) => cat?.categoryId === curr.id,
            )?.labelId
            const newVal = body.expenseDetails?.find(
              (cat) => cat?.categoryId === curr.code,
            )?.labelId
            return acc + (newVal !== oldVal ? 1 : 0)
          }, 0)
        : 0
    analytics?.track('bulk edit transaction expense categories', {
      count_updated_transactions: selectedEditableTransactions.length,
      count_updated_expense_categories: updateExpCatsCount,
    })
  }

  useChangedEffect(() => {
    if (selectedEditableTransactions.length === 0) {
      // auto close the flyout if no transactions are selected
      dispatch(closeModal())
    }
  }, [selectedEditableTransactions])

  return (
    <RightFlyout width={RightFlyoutBodyWidths.Action}>
      <Helmet>
        <title>{`Edit Transactions | ${appName}`}</title>
        <meta
          name="description"
          content={`Edit Transactions page of ${appName}`}
        />
      </Helmet>
      <span>
        <div
          css={css`
            position: relative;
            padding: 30px 48px;
            background-color: ${styleConstants.colors.white};
            display: flex;
            flex-direction: column;
            border-bottom: 1px solid ${styleConstants.colors.gray_medium};
            flex-shrink: 0;
          `}
        >
          <CloseButton
            css={css`
              flex-shrink: 0;
            `}
            variant="dark-on-light"
            onClick={(): void => {
              dispatch(closeModal())
            }}
          />
          <SvgTransaction
            css={css`
              transform: scale(0.8);
              position: absolute;
              top: -10px;
              right: -10px;
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                display: none;
              `)}
            `}
          />
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  32,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
                margin-top: 20px;
                display: flex;
                align-items: center;
                gap: 12px;
              `}
            >
              {allSelected
                ? translate('Edit Transactions')
                : translate('Edit Transactions (${count})', {
                    count: selectedEditableTransactions.length.toString(),
                  })}
              <BetaTag openLeft />
            </span>
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
                margin-top: 12px;
              `}
            >
              {translate(
                activeIntegration
                  ? 'Only transactions that have not been synced to ${erpName} can be updated'
                  : 'Any edits you make below will apply to all selected transactions',
                { erpName: activeIntegration?.platformName || '' },
              )}
            </span>
          </div>
        </div>
        <div
          css={css`
            padding: 30px 48px;
            display: flex;
            flex-direction: column;
            flex-shrink: 0;
          `}
        >
          <Box
            testId={'expense-categories-box'}
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                margin-bottom: 40px;
              `}
            >
              <span
                css={css`
                  ${getFontCss(
                    20,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )}
                `}
              >
                {translate('Expense Categories')}
              </span>
            </div>
            <TransactionExpenseDetailsForm
              expenseDetails={transactionsExpCats}
              patchingTransaction={puttingBulkTransactionExpenseData}
              onSuccessfulSubmit={(
                expenseDetails: Record<string, string>,
              ): void => {
                dispatchPatchBulkTransactionExpenseData(
                  mapExpDetailsToNulls(expenseDetails),
                )
              }}
              onCancel={(): void => {
                dispatch(closeModal())
              }}
              hideWasOnUpdate
              explicitSelectNone
            />
          </Box>
        </div>
      </span>
    </RightFlyout>
  )
}

export default UpdateBulkTransactionsFlyout
